/**
 * @copyright (C) 2025, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Charyung Kim (crkim@rsupport.com)
 *
 * @description
 *
 * @created at Wed Jan 08 2025
 **/

import { useEffect, useRef } from "react";
import { useShallow } from "zustand/react/shallow";

import { EventHandler, useEventHandlers } from "@rfice/eventhandler";

import useWhiteboardStore from "../stores/useWhiteboardStore";
import useNetworkWhiteboardStore from "../stores/useNetworkWhiteboardStore";
import { WhiteboardHook } from "./useWhiteboard";
import {
	IShareWhiteboardHandlers,
	IDrawingData,
	INetworkInit,
	INetworkDrawingStart,
	INetworkDrawingTo,
	INetworkDrawingEnd
} from "../types/types";
import Presets from "../presets";

export default function useNetworkPlayer() {
	const { whiteboard, toolState, userId, userName, windowState, whiteboardMode } = useWhiteboardStore(
		useShallow((state) => ({
			whiteboard: state.whiteboard,
			toolState: state.toolState,
			userId: state.userId,
			userName: state.userName,
			windowState: state.windowState,
			whiteboardMode: state.whiteboardMode
		}))
	);

	const { setOwnerInfo } = useNetworkWhiteboardStore(
		useShallow((state) => ({
			setOwnerInfo: state.setOwnerInfo
		}))
	);

	const whiteboardRef = useRef<WhiteboardHook>();
	const handlersRef = useRef<Partial<IShareWhiteboardHandlers>>({});
	const playersRef = useRef(new Map());
	const playerIndex = useRef(0);

	// init whiteboard
	useEffect(() => {
		whiteboardRef.current = whiteboard;
	}, [whiteboard]);

	// init player
	const init = (props: INetworkInit) => {
		const { index, ownerInfo, handlers } = props;
		playerIndex.current = index;
		setOwnerInfo(ownerInfo);
		handlersRef.current = { ...handlers };
	};

	// reset
	const reset = () => {
		for (const key of playersRef.current.keys()) whiteboardRef.current?.removeUser(key);
		handlersRef.current = {};
		playersRef.current.clear();
		setOwnerInfo(null);
	};

	// event로 player의 정보가 업데이트 되면 rfice에 필요한 데이터 전달
	useEventHandlers(
		{
			onMouseEventsDrawStart: (position: Vector2) => handlersRef.current.onDrawStart?.(getPlayerData(position)),
			onMouseEventsDraw: (positions: Vector2[]) => handlersRef.current.onDraw?.(positions),
			onMouseEventsDrawEnd: (position: Vector2) => handlersRef.current.onDrawEnd?.(position),
			onDrawingToolbarHistoryChange: (state: HistoryState) => handlersRef.current.onHistoryChange?.(state),
			onShrinkButtonClose: () => handlersRef.current.onWindowClose?.(),
			onDrawingToolbarImageClicked: () => handlersRef.current.onImageClicked?.()
		},
		[whiteboardRef, toolState, userId, userName]
	);

	// stomp를 통해 다른 플레이어들의 정보가 업데이트 될 시 drawing 처리
	const updateDrawingStart = ({ id, drawingData }: INetworkDrawingStart) => {
		playersRef.current.set(id, drawingData);
		const { canDraw, objectStyle, positions } = drawingData;

		if (positions.length <= 0) return;
		const scaledPosition = mapPositionToCanvas(positions[0], drawingData.canvasResolution);
		if (windowState === "normal") EventHandler.fire("$NetworkPlayer::Start$", id, drawingData); // indicator
		if (canDraw) whiteboardRef.current?.drawingStart(id, scaledPosition, objectStyle, true);
	};

	const updateDrawingTo = ({ id, positions }: INetworkDrawingTo) => {
		const playerData = { ...playersRef.current.get(id), positions };
		playersRef.current.set(id, playerData);

		if (positions.length <= 0) return;
		const scaledPositions = mapPositionsToCanvas(positions, playerData.canvasResolution);
		const lastIndex = scaledPositions.length - 1;
		const indicatorPosition =
			whiteboardMode === "drawing" ? scaledPositions[lastIndex] : mapPositionToRect(scaledPositions[lastIndex]);
		if (windowState === "normal") EventHandler.fire("$NetworkPlayer::Update$", id, indicatorPosition); // indicator
		if (playerData.canDraw) whiteboardRef.current?.drawingTo(id, scaledPositions, true);
	};

	const updateDrawingEnd = ({ id, position }: INetworkDrawingEnd) => {
		const { canDraw, canvasResolution } = playersRef.current.get(id);

		const scaledPosition = mapPositionToCanvas(position, canvasResolution);
		if (windowState === "normal") EventHandler.fire("$NetworkPlayer::End$", id); // indicator
		if (canDraw) whiteboardRef.current?.drawingEnd(id, scaledPosition, true);
	};

	const updateHistory = (state: HistoryState) => {
		whiteboardRef.current?.updateHistoryState[state]();
	};

	/* Utils */
	const getPlayerData = (position: Vector2): IDrawingData => {
		return {
			playerInfo: { id: userId, name: userName, index: playerIndex.current },
			canDraw: whiteboardRef.current?.getCanDraw() ?? false,
			objectStyle: whiteboardRef.current?.getObjectStyle() ?? Presets.defaultObjectStyle,
			toolState: toolState == "pointer" ? "pointerActive" : toolState,
			positions: [position],
			canvasResolution: whiteboardRef.current?.getCanvasResolution()
		};
	};

	const mapPositionToRect = (vector: Vector2) => {
		const from = whiteboardRef.current?.getCanvasResolution() || { width: 1920, height: 1080 };
		const to = whiteboardRef.current?.getCanvasRect() || { left: 0, top: 0, width: 1920, height: 1080 };

		return {
			x: to.left + (vector.x / from.width) * to.width,
			y: to.top + (vector.y / from.height) * to.height
		};
	};

	const mapPositionToCanvas = (vector: Vector2, resolution: Resolution | undefined): Vector2 => {
		const from = resolution || { width: 1920, height: 1080 };
		const to = whiteboardRef.current?.getCanvasResolution() || { width: 1920, height: 1080 };

		return {
			x: (vector.x / from.width) * to.width,
			y: (vector.y / from.height) * to.height
		};
	};

	const mapPositionsToCanvas = (vectors: Vector2[], from: Resolution): Vector2[] => {
		return vectors.map((vector) => mapPositionToCanvas(vector, from));
	};

	return {
		init,
		updateDrawingStart,
		updateDrawingTo,
		updateDrawingEnd,
		updateHistory,
		reset
	};
}
